import React from "react";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import type { UseMutateFunction } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import type { FlowVersion } from "src/api/flowTypes";
import { SchemaUpdate } from "src/api/flowVersionQueries";
import { ErrorHint } from "src/base-components/ErrorHint";
import { FormItem } from "src/base-components/FormItem";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { CaseTemplateDropdown } from "src/cases/common/CaseTemplateDropdown";
import {
  JsonApiResourceObjectCaseTemplateAttributesBaseModel,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import { SchemaEditorProps } from "src/schema/schemaEditor/types";
import { CaseTemplateInputSchemaError } from "src/schema/schemaEditor/useCaseTemplateInputSchemaValidation";

type DropdownSelection = {
  templateId: string;
  versionId: string;
};

type DropdownTemplate = JsonApiResourceObjectCaseTemplateAttributesBaseModel & {
  versions?: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel[];
};

type SelectedTemplateInfo = {
  templateId: string;
  versionId: string;
  templateName: string;
  versionName: string;
  icon: IconProp;
};

interface CaseTemplateSelectorProps extends SchemaEditorProps {
  mutateSchema: UseMutateFunction<
    FlowVersion,
    AxiosError,
    SchemaUpdate,
    unknown
  >;
  dropdownTemplates: DropdownTemplate[];
  selectedTemplate: SelectedTemplateInfo | null;
  isLoadingTemplates: boolean;
  isLoadingSchema: boolean;
  onTemplateSelection: (selection: DropdownSelection) => void;
  onTemplateClear: () => void;
  error?: CaseTemplateInputSchemaError;
}

const PLACEHOLDER_TEXT = "e.g. AML";

export const CaseTemplateSelector: React.FC<CaseTemplateSelectorProps> = ({
  isReadonly,
  dropdownTemplates,
  selectedTemplate,
  isLoadingTemplates,
  isLoadingSchema,
  onTemplateSelection,
  onTemplateClear,
  error,
}) => {
  if (isLoadingTemplates) {
    return (
      <FormItem gap="xxs" label="Select Case template" isRequired>
        <SkeletonPlaceholder height="h-8" width="w-full" />
      </FormItem>
    );
  }

  return (
    <FormItem gap="xxs" label="Select Case template" isRequired>
      {error === "archived" && (
        <ErrorHint>
          The case template version selected has been archived. Please select
          another version to associate.
        </ErrorHint>
      )}
      <CaseTemplateDropdown
        className="w-full"
        dataLoc="case-template-selector"
        disabled={isReadonly || isLoadingSchema}
        error={error === "archived" || error === "corrupted"}
        options={dropdownTemplates}
        placeholder={PLACEHOLDER_TEXT}
        selected={
          selectedTemplate
            ? {
                templateId: selectedTemplate.templateId,
                versionId: selectedTemplate.versionId,
              }
            : undefined
        }
        selectedTemplateName={selectedTemplate?.templateName}
        selectedVersionName={selectedTemplate?.versionName}
        onRefresh={error === "inconsistent" ? onTemplateSelection : undefined}
        onReset={selectedTemplate ? onTemplateClear : undefined}
        onSelect={onTemplateSelection}
      />
    </FormItem>
  );
};
